import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { TradingCard, CardBack, SET_LOGO } from '../components/TradingCard';
import { ClompApi } from '../api/ClompApi';
import { playMysticReveal, playPackOpen, playSpecialReveal } from '../components/cardSounds';
import './CardsPage.css';

const api = new ClompApi();

const TYPE_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'character', label: 'Characters' },
    { id: 'world', label: 'Worlds' },
    { id: 'item', label: 'Items' }
];
const FINISH_FILTERS = [
    { id: 'all', label: 'Any finish' },
    { id: 'trimmed', label: 'Trimmed' },
    { id: 'mystic', label: 'Mystic' }
];
const RARITY_FILTERS = [
    { id: 'all', label: 'Any rarity' },
    { id: 'common', label: 'Common' },
    { id: 'rare', label: 'Rare' },
    { id: 'epic', label: 'Epic' },
    { id: 'legendary', label: 'Legendary' }
];
// The pack shakes for at least this long while it opens, even if the server answers sooner
const OPEN_ANIMATION_MS = 700;

function timeUntil(iso, now) {
    let minutes = Math.max(1, Math.ceil((new Date(iso).getTime() - now) / 60000));
    let hours = Math.floor(minutes / 60);
    return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

export class CardsPage extends React.Component {

    state = {
        sets: [],
        cards: [],
        odds: null,
        loading: true,
        error: null,
        loggedIn: localStorage.getItem('token') !== null,
        loginExpired: false,
        collection: {},
        trimmed: {},
        mystic: {},
        // setID -> packs opened
        packsOpened: {},
        packs: null,
        tab: 'packs',
        phase: 'idle',
        // the set of the pack being opened / just opened
        packSet: null,
        revealed: [],
        setFilter: 'all',
        typeFilter: 'all',
        rarityFilter: 'all',
        finishFilter: 'all',
        selected: null,
        now: Date.now()
    };

    componentDidMount() {
        api.cards.getCatalog().then((catalog) => {
            this.setState({ sets: catalog.sets || [], cards: catalog.cards, odds: { rarityWeights: catalog.rarityWeights, trimChance: catalog.trimChance, mysticChance: catalog.mysticChance, packSize: catalog.packSize } });
            return this.state.loggedIn ? this.loadMine() : null;
        }).catch((err) => {
            console.log('Error loading cards: ', err);
            this.setState({ error: "Couldn't load the cards right now. Please try again later." });
        }).finally(() => {
            this.setState({ loading: false });
        });

        // Keep the "next pack in" countdown fresh, and pick up a new pack when it arrives
        this.timer = setInterval(() => {
            let next = this.state.packs && this.state.packs.nextPackAt;
            this.setState({ now: Date.now() });
            if (next && new Date(next).getTime() <= Date.now() && this.state.phase === 'idle') {
                this.loadMine();
            }
        }, 30000);

        window.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
        window.removeEventListener('keydown', this.onKeyDown);
    }

    // Space turns over every card still face-down in an opened pack, wherever the focus is
    onKeyDown = (e) => {
        if (e.key !== ' ' || e.repeat || e.ctrlKey || e.altKey || e.metaKey) {
            return;
        }
        let { tab, phase, revealed, selected } = this.state;
        if (tab !== 'packs' || phase !== 'revealed' || selected || revealed.every((r) => r.flipped)) {
            return;
        }
        // stops the page scrolling, and a focused card or button from also taking the key
        e.preventDefault();
        this.revealAll();
    }

    loadMine() {
        return api.cards.getMine().then((mine) => {
            this.setState({ collection: mine.collection, trimmed: mine.trimmed || {}, mystic: mine.mystic || {}, packsOpened: mine.packsOpened || {}, packs: mine.packs });
        }).catch((err) => this.handleError(err));
    }

    handleError(err) {
        let status = err.response && err.response.status;
        if (status === 401) {
            this.setState({ loggedIn: false, loginExpired: true, phase: 'idle' });
        } else if (status === 409) {
            this.setState({ packs: err.response.data.packs, phase: 'idle' });
        } else {
            console.log('Card error: ', err);
            this.setState({ error: 'Something went wrong. Please try again.', phase: 'idle' });
        }
    }

    cardById(id) {
        return this.state.cards.find((card) => card.id === id);
    }

    // How many cards are in a card's set (each set is numbered on its own)
    setSize(card) {
        return this.state.cards.filter((other) => other.set === card.set).length;
    }

    openPack = (setID) => {
        playPackOpen();
        this.setState({ phase: 'opening', packSet: setID, revealed: [], error: null });
        let wait = new Promise((resolve) => setTimeout(resolve, OPEN_ANIMATION_MS));
        Promise.all([api.cards.openPack(setID), wait]).then(([result]) => {
            let collection = { ...this.state.collection };
            let trimmed = { ...this.state.trimmed };
            let mystic = { ...this.state.mystic };
            result.cards.forEach((card) => {
                collection[card.id] = (collection[card.id] || 0) + 1;
                if (card.trimmed) {
                    trimmed[card.id] = (trimmed[card.id] || 0) + 1;
                }
                if (card.mystic) {
                    mystic[card.id] = (mystic[card.id] || 0) + 1;
                }
            });
            // Only the first copy of a new card (or first Trimmed or Mystic copy) in the pack gets a badge
            let newLeft = [...result.newCards];
            let newTrimsLeft = [...(result.newTrims || [])];
            let newMysticsLeft = [...(result.newMystics || [])];
            let revealed = result.cards.map((card) => {
                let isNew = newLeft.includes(card.id);
                newLeft = newLeft.filter((n) => n !== card.id);
                let isNewTrim = card.trimmed && newTrimsLeft.includes(card.id);
                if (isNewTrim) {
                    newTrimsLeft = newTrimsLeft.filter((n) => n !== card.id);
                }
                let isNewMystic = Boolean(card.mystic) && newMysticsLeft.includes(card.id);
                if (isNewMystic) {
                    newMysticsLeft = newMysticsLeft.filter((n) => n !== card.id);
                }
                return { id: card.id, trimmed: card.trimmed, mystic: Boolean(card.mystic), isNew, isNewTrim, isNewMystic, flipped: false };
            });
            let packsOpened = { ...this.state.packsOpened, [setID]: (this.state.packsOpened[setID] || 0) + 1 };
            this.setState({ collection, trimmed, mystic, packsOpened, packs: result.packs, revealed, phase: 'revealed' });
        }).catch((err) => this.handleError(err));
    }

    // Legendary and Trimmed cards get their own sound when they turn over
    isSpecial(r) {
        let card = this.cardById(r.id);
        return r.trimmed || (card && card.rarity === 'legendary');
    }

    // The sound for cards turning over together: Mystic has its own, and wins over the Legendary and Trimmed chime
    playRevealSound(turning) {
        if (turning.some((r) => r.mystic)) {
            playMysticReveal();
        } else if (turning.some((r) => this.isSpecial(r))) {
            playSpecialReveal();
        }
    }

    flip(index) {
        let r = this.state.revealed[index];
        if (r && !r.flipped) {
            this.playRevealSound([r]);
        }
        this.setState({ revealed: this.state.revealed.map((r, i) => (i === index ? { ...r, flipped: true } : r)) });
    }

    revealAll = () => {
        this.playRevealSound(this.state.revealed.filter((r) => !r.flipped));
        this.setState({ revealed: this.state.revealed.map((r) => ({ ...r, flipped: true })) });
    }

    renderLoginPrompt(action) {
        return (
            <div className="cards-login">
                <p>
                    {this.state.loginExpired ? 'Your login has expired. ' : ''}
                    Log in to {action}.
                </p>
                <Link className="cards-btn cards-btn-primary" to="/login">Log in</Link>
            </div>
        );
    }

    renderPacks() {
        let { sets, packs, phase, packSet, revealed, now } = this.state;
        if (!this.state.loggedIn) {
            return (
                <section className="cards-pack-area">
                    <div className="cards-pack-row" aria-hidden="true">
                        {sets.map((set) => (
                            <div key={set.id} className={`cards-pack cards-pack-${set.id}`}><PackFace set={set} /></div>
                        ))}
                    </div>
                    <p className="cards-pack-status">Pack odds <PackOdds odds={this.state.odds} /></p>
                    {this.renderLoginPrompt('open packs and start your collection')}
                </section>
            );
        }

        if (phase === 'revealed') {
            let allFlipped = revealed.every((r) => r.flipped);
            return (
                <section className="cards-pack-area">
                    <ul className="cards-reveal">
                        {revealed.map((r, i) => (
                            <li key={i} className={`cards-flip${r.flipped ? ' is-flipped' : ''}`} style={{ animationDelay: `${i * 90}ms` }}>
                                <div className="cards-flip-inner">
                                    <div className="cards-flip-face cards-flip-back">
                                        <button type="button" className="cards-flip-btn" onClick={() => this.flip(i)} aria-label={`Reveal card ${i + 1}`}>
                                            <CardBack set={packSet} />
                                        </button>
                                    </div>
                                    <div className="cards-flip-face cards-flip-front">
                                        <TradingCard card={this.cardById(r.id)} total={this.setSize(this.cardById(r.id))} trimmed={r.trimmed} mystic={r.mystic} isNew={r.isNew} isNewTrim={r.isNewTrim} isNewMystic={r.isNewMystic} onClick={r.flipped ? () => this.setState({ selected: r.id }) : undefined} />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="cards-actions">
                        {allFlipped ?
                            <>
                                <button type="button" className="cards-btn cards-btn-primary" onClick={() => this.setState({ phase: 'idle' })}>Open another pack</button>
                                <button type="button" className="cards-btn cards-btn-ghost" onClick={() => this.setState({ tab: 'collection', phase: 'idle' })}>View collection</button>
                            </>
                            :
                            <button type="button" className="cards-btn cards-btn-ghost" onClick={this.revealAll}>Reveal all</button>
                        }
                    </div>
                </section>
            );
        }

        let unlimited = Boolean(packs && packs.unlimited);
        let available = unlimited ? Infinity : packs ? packs.available : 0;
        return (
            <section className="cards-pack-area">
                <div className="cards-pack-row">
                    {sets.map((set) => (
                        <button key={set.id} type="button" className={`cards-pack cards-pack-${set.id}${phase === 'opening' && packSet === set.id ? ' is-opening' : ''}`}
                            onClick={() => this.openPack(set.id)} disabled={available < 1 || phase === 'opening'} aria-label={`Open a ${set.name} pack`}>
                            <PackFace set={set} />
                        </button>
                    ))}
                </div>
                <p className="cards-pack-status">
                    {unlimited ? 'Open as many packs as you like.' : packs ?
                        <>
                            <strong>{available} of {packs.max}</strong> packs ready
                            {packs.nextPackAt ? <> · next pack in {timeUntil(packs.nextPackAt, now)}</> : null}
                        </>
                        : 'Checking your packs...'}
                    <PackOdds odds={this.state.odds} />
                </p>
                <p className="cards-pack-hint">
                    {phase === 'opening' ? 'Opening...' : available > 0 ? 'Click a pack to open it. Each pack has 5 cards from its own game, and the last one is always Rare or better. Any card can come Trimmed in gold, and a rare few come Mystic.' : 'No packs right now. A new one arrives every few hours.'}
                </p>
            </section>
        );
    }

    renderCollection() {
        let { sets, cards, collection, trimmed, mystic, packsOpened, setFilter, typeFilter, rarityFilter, finishFilter } = this.state;
        let setFilters = [{ id: 'all', label: 'All games' }, ...sets.map((set) => ({ id: set.id, label: set.name }))];
        let owned = cards.filter((card) => collection[card.id]).length;
        let ownedTrimmed = cards.filter((card) => trimmed[card.id]).length;
        let ownedMystic = cards.filter((card) => mystic[card.id]).length;
        let owns = { trimmed: trimmed, mystic: mystic };
        // every pack, or just the chosen game's
        let filteredSet = sets.find((set) => set.id === setFilter);
        let opened = filteredSet ? (packsOpened[filteredSet.id] || 0) : Object.values(packsOpened).reduce((sum, n) => sum + n, 0);
        let shown = cards.filter((card) => (setFilter === 'all' || card.set === setFilter)
            && (typeFilter === 'all' || card.type === typeFilter)
            && (rarityFilter === 'all' || card.rarity === rarityFilter)
            && (finishFilter === 'all' || owns[finishFilter][card.id]));

        return (
            <section>
                {!this.state.loggedIn ? this.renderLoginPrompt('see your collection') : null}
                <div className="cards-progress">
                    <div className="cards-progress-label">
                        <strong>{owned} / {cards.length}</strong> cards collected
                        {ownedTrimmed > 0 ? <> · <span className="cards-trim-count">{ownedTrimmed} Trimmed</span></> : null}
                        {ownedMystic > 0 ? <> · <span className="cards-mystic-count">{ownedMystic} Mystic</span></> : null}
                    </div>
                    <div className="cards-progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax={cards.length} aria-valuenow={owned}>
                        <div style={{ width: `${cards.length ? (owned / cards.length) * 100 : 0}%` }}></div>
                    </div>
                    {this.state.loggedIn ?
                        <div className="cards-packs-opened">
                            <strong>{opened.toLocaleString()}</strong> {filteredSet ? `${filteredSet.name} ` : ''}{opened === 1 ? 'pack' : 'packs'} opened
                        </div>
                        : null}
                </div>
                <div className="cards-filters">
                    {sets.length > 1 ? <FilterGroup label="Game" options={setFilters} value={setFilter} onChange={(id) => this.setState({ setFilter: id })} /> : null}
                    <FilterGroup label="Type" options={TYPE_FILTERS} value={typeFilter} onChange={(id) => this.setState({ typeFilter: id })} />
                    <FilterGroup label="Rarity" options={RARITY_FILTERS} value={rarityFilter} onChange={(id) => this.setState({ rarityFilter: id })} />
                    <FilterGroup label="Finish" options={FINISH_FILTERS} value={finishFilter} onChange={(id) => this.setState({ finishFilter: id })} />
                </div>
                {shown.length === 0 ?
                    <p className="cards-status">{finishFilter === 'trimmed' && ownedTrimmed === 0 ? 'No Trimmed cards yet. Every card you pull has a small chance to come Trimmed.'
                        : finishFilter === 'mystic' && ownedMystic === 0 ? 'No Mystic cards yet. A Trimmed card has a small chance to come Mystic instead.'
                        : 'No cards match those filters.'}</p>
                    :
                    <ul className="cards-grid">
                        {shown.map((card) => (
                            <li key={card.id}>
                                {collection[card.id] ?
                                    <TradingCard card={card} total={this.setSize(card)} count={collection[card.id]} trimmed={Boolean(trimmed[card.id])} mystic={finishFilter !== 'trimmed' && Boolean(mystic[card.id])} onClick={() => this.setState({ selected: card.id })} />
                                    :
                                    <TradingCard card={card} total={this.setSize(card)} locked />
                                }
                            </li>
                        ))}
                    </ul>
                }
            </section>
        );
    }

    renderSelected() {
        let card = this.cardById(this.state.selected);
        if (!card) {
            return null;
        }
        let close = () => this.setState({ selected: null });
        let copies = this.state.collection[card.id] || 0;
        let trimmedCopies = this.state.trimmed[card.id] || 0;
        let mysticCopies = this.state.mystic[card.id] || 0;
        return (
            <div className="cards-modal" role="dialog" aria-modal="true" aria-label={card.name} onClick={close} onKeyDown={(e) => e.key === 'Escape' && close()}>
                <div className="cards-modal-body" onClick={(e) => e.stopPropagation()}>
                    <TradingCard card={card} total={this.setSize(card)} count={copies} trimmed={trimmedCopies > 0} mystic={mysticCopies > 0} detailed />
                    <p className="cards-modal-owned">
                        You have {copies} {copies === 1 ? 'copy' : 'copies'}{trimmedCopies > 0 ? `, ${trimmedCopies} Trimmed` : ''}{mysticCopies > 0 ? `, ${mysticCopies} Mystic` : ''}.
                    </p>
                    <button type="button" className="cards-btn cards-btn-ghost" onClick={close} autoFocus>Close</button>
                </div>
            </div>
        );
    }

    render() {
        let { loading, error, tab, cards, collection } = this.state;
        let owned = cards.filter((card) => collection[card.id]).length;

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="cards-page">
                    <div className="cards-page-header">
                        <p className="cards-eyebrow">Clomp games</p>
                        <h1>Trading Cards</h1>
                        <p className="cards-intro">Collect the characters, worlds and items of Solarflare and Space Jump. Open packs to find new cards.</p>
                    </div>

                    <div className="cards-tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={tab === 'packs'} className={tab === 'packs' ? 'is-active' : ''} onClick={() => this.setState({ tab: 'packs' })}>Open packs</button>
                        <button type="button" role="tab" aria-selected={tab === 'collection'} className={tab === 'collection' ? 'is-active' : ''} onClick={() => this.setState({ tab: 'collection' })}>
                            Collection{cards.length ? ` (${owned}/${cards.length})` : ''}
                        </button>
                    </div>

                    {error ? <p className="cards-status cards-error">{error}</p> : null}
                    {loading ?
                        <p className="cards-status">Loading cards...</p>
                        :
                        cards.length > 0 ? (tab === 'packs' ? this.renderPacks() : this.renderCollection()) : null
                    }
                </div>
                {this.renderSelected()}
            </>
        );
    }

}

function PackFace({ set }) {
    return (
        <span className="cards-pack-face">
            <span className="cards-pack-brand">{set.name}</span>
            <img src={SET_LOGO[set.id] || SET_LOGO.solarflare} alt="" />
            <span className="cards-pack-label">Booster Pack</span>
            <span className="cards-pack-count">5 cards</span>
        </span>
    );
}

const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary'];

// 0.55 -> "55%", 0.6667 -> "66.7%"
function percent(chance) {
    return `${Math.round(chance * 1000) / 10}%`;
}

// The (i) next to the pack: shows the rarity and Trimmed odds on hover or keyboard focus, and toggles on tap
function PackOdds({ odds }) {
    let [open, setOpen] = React.useState(false);
    // Escape hides the panel even while the (i) keeps focus or the pointer stays over it, until it's shown again
    let [dismissed, setDismissed] = React.useState(false);
    if (!odds || !odds.rarityWeights) {
        return null;
    }
    let weights = odds.rarityWeights;
    let rareOrBetter = RARITY_ORDER.slice(1).reduce((sum, r) => sum + weights[r], 0);
    let packSize = odds.packSize || 5;
    let trim = odds.trimChance || 0;
    let trimPerPack = 1 - Math.pow(1 - trim, packSize);
    let mystic = odds.mysticChance || 0;

    return (
        <span
            className={`cards-odds${open ? ' is-open' : ''}${dismissed ? ' is-dismissed' : ''}`}
            onMouseEnter={() => setDismissed(false)}
            onMouseLeave={() => setOpen(false)}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    setOpen(false);
                    setDismissed(true);
                }
            }}
        >
            <button type="button" className="cards-odds-btn" aria-label="Pack odds" aria-describedby="cards-odds-pop" aria-expanded={open}
                onFocus={() => setDismissed(false)} onBlur={() => setOpen(false)}
                onClick={() => { setDismissed(false); setOpen(!open); }}>i</button>
            <span className="cards-odds-pop" id="cards-odds-pop" role="tooltip">
                <span className="cards-odds-title">Pack odds</span>
                <span className="cards-odds-table">
                    <span className="cards-odds-head"></span>
                    <span className="cards-odds-head">Cards 1–{packSize - 1}</span>
                    <span className="cards-odds-head">Card {packSize}</span>
                    {RARITY_ORDER.map((r) => (
                        <React.Fragment key={r}>
                            <span className={`cards-odds-rarity cards-odds-${r}`}>{r[0].toUpperCase() + r.slice(1)}</span>
                            <span>{percent(weights[r])}</span>
                            <span>{r === 'common' ? '–' : percent(weights[r] / rareOrBetter)}</span>
                        </React.Fragment>
                    ))}
                </span>
                {trim > 0 ?
                    <span className="cards-odds-trim">
                        <strong>Trimmed:</strong> 1 in {Math.round(1 / trim)} cards ({percent(trim)}), any rarity.
                        About {percent(trimPerPack)} of packs have at least one.
                    </span>
                    : null}
                {trim > 0 && mystic > 0 ?
                    <span className="cards-odds-trim cards-odds-mystic">
                        <strong>Mystic:</strong> 1 in {Math.round(1 / mystic)} Trimmed cards comes Mystic instead,
                        about 1 in {Math.round(1 / (trim * mystic)).toLocaleString()} cards.
                    </span>
                    : null}
            </span>
        </span>
    );
}

function FilterGroup({ label, options, value, onChange }) {
    return (
        <div className="cards-filter-group" role="group" aria-label={label}>
            {options.map((option) => (
                <button key={option.id} type="button" className={value === option.id ? 'is-active' : ''} aria-pressed={value === option.id} onClick={() => onChange(option.id)}>
                    {option.label}
                </button>
            ))}
        </div>
    );
}
